import React from 'react';
import {Button, DisplayMode} from '@taopix/taopix-design-system';
import useSettings from '../../Hooks/useSettings';
import {AIMode, AssistantType, ExternalAssetAdapter} from '@taopix/taopix-core';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useSelector} from 'react-redux';
import {ExternalAssetSelector, LayoutSelector} from '../../Selectors';
import {useTranslation} from 'react-i18next';
import {useAssistant} from '../Context/Assistant/AssistantContext';
import {useTaopix} from '../Context/TaopixContext';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import AppMenu, {AppMenuItem} from '../AppMenu/AppMenu';
import SaveButton from '../Save/SaveButton';
import {ProductLayout} from '@taopix/taopix-core/dist/types/types';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import Logo from '../AppHeader/Logo';
import OrderButton from '../AppHeader/OrderButton';
import AppHeader from '../AppHeader/AppHeader';
import ProjectInfoButton, {ProjectInfoButtonProps} from '../AppHeader/ProjectInfoButton';
import {useExperience} from '../Experience/ExperienceProvider';
import {AssistantCommonProps, AssistantHeaderProps} from './Assistant';
import {getUrl} from '../../Utils/Common';

export const AssistantHeader = ({
  aiMode,
  displayMode,
  onContinueInEditor = () => {},
  ...props
}: {aiMode?: AIMode; projectPrice?: string; pageCount?: number} & AssistantCommonProps &
  AssistantHeaderProps) => {
  const taopix = useTaopix();
  const assistant = useAssistant();
  const ui = useUiState();
  const settings = useSettings();
  const {experience} = useExperience();
  const {t} = useTranslation(['assistants', 'optionsandextras']);
  const auth = settings.state?.authentication;
  const appMenuSaveButton = experience.SETTINGS.appMenu.saveButton || false;
  const userFlow = useUserFlowState();
  const externalAssets = useSelector(ExternalAssetSelector);
  const isPreviewStep = userFlow
    .getCurrentStep(UserFlowType.Assistant)
    .includes(UserFlowStep.AssistantPreview);
  const pricingAiMode =
    typeof aiMode === 'undefined'
      ? AssistantType.SmartDesign === experience.WIZARD.designAssistantType.designAssistantType &&
        !userFlow.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantWelcome)
        ? AIMode.On
        : AIMode.Off
      : aiMode;

  const onClickItem = (value: AppMenuItem) => {
    switch (value.toLowerCase()) {
      case AppMenuItem.Language:
        ui.dispatch(UserInterfaceActions.setLanguageDialogOpen(true));
        break;
      case AppMenuItem.SignIn:
        userFlow.activateUserFlow(UserFlowType.Auth);
        userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Login}`);
        break;
      case AppMenuItem.Register:
        userFlow.activateUserFlow(UserFlowType.Auth);
        userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Register}`);
        break;
      case AppMenuItem.SignOut:
        userFlow.activateUserFlow(UserFlowType.Auth);
        userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Logout}`);
        break;
      case AppMenuItem.About:
        window.open(getUrl(settings.state.urls.about), '_blank');
        break;
      case AppMenuItem.Help:
        window.open(getUrl(settings.state.urls.help), '_blank');
        break;
      case AppMenuItem.Exit:
        window.location.href = getUrl(experience.SETTINGS.logoLinkUrl.logoLinkUrl);
        break;
      default:
    }
  };

  // get the layout from the store
  const productLayout = useSelector(LayoutSelector);

  const projectPrice = taopix.core.get('pricingEngine').formatCurrencyNumber(
    assistant.getPricingEngine(pricingAiMode).calculateProjectPrice(
      assistant.state.pageCount > -1
        ? assistant.state.pageCount
        : assistant.state.settings.pageCountModeStep.defaultPages,
      ExternalAssetAdapter(externalAssets, {
        usagepicture: productLayout?.usagepicture || {},
      } as ProductLayout),
      0,
      0
    ),
    assistant.currency
  );

  const leftSideHeaderContent = (
    <>
      {DisplayMode.Fullscreen === displayMode && (
        <AppMenu
          showSave={false}
          showShare={false}
          onClickItem={(item: string) => {
            onClickItem(item as AppMenuItem);
          }}
          authentication={auth || {}}
        />
      )}
      {DisplayMode.Fullscreen === displayMode &&
        !ui.state.isSmallScreen &&
        experience.WIZARD.header.logo && <Logo />}
    </>
  );

  const projectInfoExperience: ProjectInfoButtonProps['perms'] = {
    ...experience.WIZARD.header,
    optionsAndExtrasButton:
      DisplayMode.Positioned === displayMode
        ? false
        : experience.WIZARD.header.optionsAndExtrasButton,
  };

  const rightSideHeaderContent = (
    <>
      {DisplayMode.Fullscreen === displayMode &&
        isPreviewStep &&
        experience.WIZARD.previewStep.save &&
        (!ui.state.isSmallScreen || (ui.state.isSmallScreen && !appMenuSaveButton)) && (
          // Show the save button in large screen if enabled in the assistant preview step configuration
          // Only show here on small screen if enabled AND the app menu save button is hidden
          <SaveButton label={t('*:str_ButtonSave')} size={'small'} stacked />
        )}
      <ProjectInfoButton
        id={'assistant'}
        perms={projectInfoExperience}
        projectPrice={projectPrice}
        projectName={settings.state.project.name}
        productName={productLayout.name}
        pageCount={(() => {
          let pageCount = props.pageCount ? props.pageCount : productLayout.pagecount;

          if (
            AssistantType.SmartDesign === experience.WIZARD.designAssistantType.designAssistantType
          ) {
            pageCount = 2 * Math.ceil(pageCount / 2);
          }
          return pageCount;
        })()}
      />
      {DisplayMode.Fullscreen === displayMode &&
        experience.WIZARD.previewStep.editButton &&
        isPreviewStep && (
          <Button
            label={
              ui.state.isSmallScreen
                ? experience.WIZARD.previewStep.orderButton
                  ? t('*:str_ButtonEdit')
                  : t('*:str_ButtonContinue')
                : experience.WIZARD.previewStep.orderButton
                ? t('str_ButtonGoToEditor')
                : t('str_ButtonContinueToEditor') // If there is not order button, use 'Continue to editor'
            }
            onClick={() => {
              onContinueInEditor();
            }}
            buttonStyle={experience.WIZARD.previewStep.orderButton ? 'tertiary' : 'primary'}
            labelLines={1}
          />
        )}

      {DisplayMode.Fullscreen === displayMode &&
        isPreviewStep &&
        experience.WIZARD.previewStep.orderButton && <OrderButton />}
    </>
  );

  return (
    <AppHeader
      id="assistant-app-header"
      leftSideContent={leftSideHeaderContent}
      rightSideContent={rightSideHeaderContent}
    />
  );
};
